import { useState, useEffect } from "react";
import {
    Box, Paper, Typography, Dialog, DialogTitle, DialogContent,
    Button, IconButton, Grid, Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Crumbs } from "../Crumbs.jsx";
import Helmet from "react-helmet";
import UserContentBox from "./Account/UserContentBox";
import { useAuth } from "../Auth/AuthPage.jsx";
import { Spotify } from "react-spotify-embed";
import { InstagramEmbed } from "react-social-media-embed";
import Avatar from "@mui/material/Avatar";
import SubNav from "./Community/SubNav.jsx";

const desc = "The Austin 2-Step community shares their best!";
const title = "Austin 2-Step Community Contributions";
const BACKEND_HOST = process.env.REACT_APP_BACKEND_HOST;

const meta = <Helmet>
    <title>{title}</title>
    <meta name="description" content={desc} />
    <meta charSet="utf-8"/>
    <meta property="og:description" content={desc}/>
    <meta property="twitter:title" content={title}/>
    <meta property="twitter:description" content={desc}/>
    <title>{title}</title>
    <meta property="description" content={desc} />
    <link rel="canonical" href={"/community"}/>
</Helmet>

const CommunityRecentContent = () => {
    const [playlists, setPlaylists] = useState([]);
    const [instas, setInstas] = useState([]);
    const [links, setLinks] = useState([]);
    const [pinnedLinks, setPinnedLinks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchContent = async () => {
            setLoading(true);
            const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
            try {
                const [plRes, igRes, linkRes] = await Promise.all([
                    fetch(`${BACKEND_HOST}/playlists?createdAt_gte=${since}&_sort=createdAt:desc&populate=profile.pic`),
                    fetch(`${BACKEND_HOST}/user-instagram-posts?createdAt_gte=${since}&_sort=createdAt:desc&populate=profile.pic`),
                    fetch(`${BACKEND_HOST}/links?createdAt_gte=${since}&pinned_ne=true&_sort=createdAt:desc&populate=user_profile.pic`)
                ]);
                const plData = plRes.ok ? await plRes.json() : [];
                const igData = igRes.ok ? await igRes.json() : [];
                const linkData = linkRes.ok ? await linkRes.json() : [];
                setPlaylists(plData.data);
                setInstas(igData.data);
                setLinks(linkData.data);
                setPinnedLinks(linkData.data.filter(link => link.pinned));
            } catch {
                setPlaylists([]);
                setInstas([]);
                setLinks([]);
                setPinnedLinks([]);
            }
            setLoading(false);
        };
        fetchContent();
    }, []);

    if (loading) {
        return (
            <Box my={2}>
                <Typography variant="h6">Recent Community Posts</Typography>
                <Typography variant="body2" color="text.secondary">Loading...</Typography>
            </Box>
        );
    }

    if (playlists.length === 0 && instas.length === 0 && links.length === 0) {
        return (
            <Box my={2}>
                <Typography variant="h6">Recent Community Posts</Typography>
                <Alert severity="info" sx={{ mt: 2 }}>No community content from the last week yet.</Alert>
            </Box>
        );
    }

    return (
        <Box my={2}>
            {/* Pinned Links Section */}
            {pinnedLinks.length > 0 && (
                <Box mb={4}>

                    <Grid container spacing={2}>
                        {pinnedLinks.map((link) => (
                            <Grid item xs={12} md={6} lg={4} key={`pinned-${link.id}`}>
                                <Paper sx={{ 
                                    p: 2, 
                                    height: "100%",
                                    border: '2px solid',
                                    borderColor: 'primary.main',
                                 
                                    position: 'relative'
                                }}>
                                    {/* Pinned indicator */}
                                    <Box 
                                        sx={{ 
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,

                                            borderRadius: '50%',
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px'
                                        }}
                                    >
                                        📌
                                    </Box>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        {link.user_profile?.pic?.formats?.thumbnail?.url ? (
                                            <Avatar
                                                src={link.user_profile.pic.formats.thumbnail.url}
                                                alt={link.user_profile.title}
                                                sx={{ width: 32, height: 32, mr: 1 }}
                                            />
                                        ) : (
                                            <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                                                {link.user_profile?.title?.[0] || "?"}
                                            </Avatar>
                                        )}
                                        {link.user_profile?.name && (
                                            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                                {link.user_profile.name}
                                            </Typography>
                                        )}
                                    </Box>
                                    <div>
                                        <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold' }}>
                                            {link.title}
                                        </Typography>
                                    </div>
                                    {link.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1, mb: 1 }}>
                                            {link.description}
                                        </Typography>
                                    )}
                                    <Box sx={{ ml: 1, mb: 1 }}>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            size="small"
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Visit Featured Link
                                        </Button>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                        {link.createdAt ? `Posted ${new Date(link.createdAt).toLocaleString()}` : ""}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
            <SubNav />
            <Typography variant="h6" gutterBottom>Recent Community Posts</Typography>
            <Grid container spacing={2}>
                {playlists.map((pl) => (
                    <Grid item xs={12} md={6} lg={4} key={`playlist-${pl.id}`}>
                        <Paper sx={{ p: 2, height: "100%" }}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {pl.profile?.pic?.formats?.thumbnail?.url ? (
                                    <Avatar
                                        src={pl.profile.pic.formats.thumbnail.url}
                                        alt={pl.profile.name}
                                        sx={{ width: 32, height: 32, mr: 1 }}
                                    />
                                ) : (
                                    <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                                        {pl.profile?.name?.[0] || "?"}
                                    </Avatar>
                                )}
                                {pl.profile?.name && (
                                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                        {pl.profile.name}
                                    </Typography>
                                )}
                            </Box>
                            <div>
                                <Typography variant="subtitle1" sx={{ ml: 1 }}>{pl.title}</Typography>
                            </div>
                            <Spotify link={pl.link} />
                            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                {pl.createdAt ? `Posted ${new Date(pl.createdAt).toLocaleString()}` : ""}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
                {instas.map((ig) => (
                    <Grid item xs={12} md={6} lg={4} key={`insta-${ig.id}`}>
                        <Paper sx={{ p: 2, height: "100%" }}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {ig.profile?.pic?.formats?.thumbnail?.url ? (
                                    <Avatar
                                        src={ig.profile.pic.formats.thumbnail.url}
                                        alt={ig.profile.name}
                                        sx={{ width: 32, height: 32, mr: 1 }}
                                    />
                                ) : (
                                    <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                                        {ig.profile?.name?.[0] || "?"}
                                    </Avatar>
                                )}
                                {ig.profile?.name && (
                                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                        {ig.profile.name}
                                    </Typography>
                                )}
                            </Box>
                            <div>
                                <Typography variant="subtitle1" sx={{ ml: 1 }}>{ig.title}</Typography>
                            </div>
                            <InstagramEmbed url={ig.link} width={328} />
                            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                {ig.createdAt ? `Posted ${new Date(ig.createdAt).toLocaleString()}` : ""}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
                {links.map((link) => (
                    <Grid item xs={12} md={6} lg={4} key={`link-${link.id}`}>
                        <Paper sx={{ p: 2, height: "100%" }}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {link.user_profile?.pic?.formats?.thumbnail?.url ? (
                                    <Avatar
                                        src={link.user_profile.pic.formats.thumbnail.url}
                                        alt={link.user_profile.title}
                                        sx={{ width: 32, height: 32, mr: 1 }}
                                    />
                                ) : (
                                    <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                                        {link.user_profile?.title?.[0] || "?"}
                                    </Avatar>
                                )}
                                {link.user_profile?.title && (
                                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                        {link.user_profile.title}
                                    </Typography>
                                )}
                            </Box>
                            <div>
                                <Typography variant="subtitle1" sx={{ ml: 1 }}>{link.title}</Typography>
                            </div>
                            {link.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, mb: 1 }}>
                                    {link.description}
                                </Typography>
                            )}
                            <Box sx={{ ml: 1, mb: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Visit Link
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                {link.createdAt ? `Posted ${new Date(link.createdAt).toLocaleString()}` : ""}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

const CommunityPage = () => {
    const { currentUserProfile } = useAuth();
    const [dialogOpen, setDialogOpen] = useState(false);
    console.log(currentUserProfile)

    return <Box>
        {meta}
        <Crumbs crumbs={[
            { 'title': 'Community' }
        ]} />
        <Box style={{ borderRadius: "10px" }} mb={2}>
            <Paper>
                <Box p={1} textAlign="center">
                    <Typography variant={"body2"}>
                        Austin 2-Step is all about community! Share your favorite Instagram posts, Spotify playlists, and useful links here.
                    </Typography>
                </Box>
                <Box p={1} textAlign="center">
                    <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)}>
                        Share with the Community
                    </Button>
                </Box>
            </Paper>
        </Box>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Share with the Community
                <IconButton
                    aria-label="close"
                    onClick={() => setDialogOpen(false)}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                        ml: 2,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
    
                <UserContentBox currentUserProfile={currentUserProfile} />
            </DialogContent>
        </Dialog>
        {/* --- Recent Community Content Section --- */}
        <CommunityRecentContent />
    </Box>
}

export default CommunityPage;